import { Archive, ArchiveRestore, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmPopover } from '~/components/common/confirm-popover';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface ArchiveBulkActionsProps {
  /** Number of selected rows. */
  count: number;
  /** True in the Archived view — the action becomes Unarchive. */
  archived: boolean;
  isPending: boolean;
  onConfirm: () => void;
  /** Deselects everything. Renders the × on the count chip when provided. */
  onClear?: () => void;
  /**
   * Drafts inside the selection (experiments only). While `count` > 0 the archive
   * confirmation also explains that drafts have nothing worth archiving and offers
   * to discard them instead — the same DELETE the per-row "Discard draft" performs,
   * run for each selected draft. Ignored in the Archived view (unarchive is instant). */
  drafts?: {
    count: number;
    isPending: boolean;
    onDiscard: () => void;
  };
  /** `data-testid` root; derives `-count`, `-clear-button`, `-action-button`, `-confirm-popover`. */
  testId?: string;
}

/**
 * Inline bulk-archive controls for a list's header row, shown while rows are
 * selected: a count chip (with a Clear ×) and Archive selected (light
 * confirmation) or Unarchive selected (instant — never refused). Sized to the
 * header's 36px search box so ticking rows changes nothing about the page's
 * layout — the controls slide into the existing row instead of opening a new
 * one. The owning hook runs the batch and toasts the server's summary; refused
 * rows stay selected so the operator can act on them. When the selection holds
 * drafts (`drafts`), the archive confirmation also offers to discard them instead.
 */
export function ArchiveBulkActions({
  count,
  archived,
  isPending,
  onConfirm,
  onClear,
  drafts,
  testId = 'archive-bulk-actions',
}: ArchiveBulkActionsProps) {
  const { t } = useTranslation('common');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const label = archived
    ? t('listArchive.bulk.unarchiveSelected')
    : t('listArchive.bulk.archiveSelected');
  const Icon = archived ? ArchiveRestore : Archive;
  // Draft offer: only meaningful when archiving (unarchive never confirms) and
  // only when the selection actually holds drafts. "All" vs "some" changes both
  // the notice and what happens to the rest of the selection.
  const draftCount = !archived && drafts ? drafts.count : 0;
  const draftsIsPending = drafts?.isPending ?? false;
  const draftNotice =
    draftCount === 0
      ? null
      : draftCount === count
        ? t('listArchive.bulk.drafts.noticeAll', { count: draftCount })
        : t('listArchive.bulk.drafts.noticeSome', { count: draftCount });

  // Filled primary, not outline: beside the quiet count chip this is the one
  // thing to do with a selection, and an outlined button read as inert next to
  // the search box it now sits with.
  const button = (
    <Button
      type="button"
      variant="default"
      size="sm"
      data-testid={`${testId}-action-button`}
      // Discarding drafts runs from the same control, so its progress shows here too.
      isLoading={isPending || draftsIsPending}
      className="cursor-pointer gap-1.5 disabled:cursor-not-allowed"
      onClick={archived ? onConfirm : undefined}
    >
      {!isPending && !draftsIsPending && <Icon className="h-4 w-4" aria-hidden="true" />}
      {label}
    </Button>
  );

  return (
    // Answers the tick that revealed it: a short fade plus a 4px slide from the
    // right, suppressed under reduced motion.
    <div
      data-testid={testId}
      className="animate-in fade-in slide-in-from-right-1 flex items-center gap-2 duration-150 motion-reduce:animate-none"
    >
      {/* Count chip — the selection state as one object: how many, and the way out. */}
      <span
        data-testid={`${testId}-count`}
        className={cn(
          'bg-selected-row text-primary inline-flex h-9 items-center gap-0.5 rounded-md pl-3 text-sm font-medium tabular-nums',
          onClear ? 'pr-1' : 'pr-3',
        )}
      >
        {t('listArchive.bulk.selected', { count })}
        {onClear && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('listArchive.bulk.clear')}
            data-testid={`${testId}-clear-button`}
            className="text-primary hover:bg-primary/10 hover:text-primary h-7 w-7 cursor-pointer rounded-sm"
            onClick={onClear}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </span>
      {archived ? (
        button
      ) : (
        <ConfirmPopover
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('listArchive.bulk.confirmTitle', { count })}
          description={
            draftNotice ? (
              <>
                <p>{t('listArchive.bulk.confirmBody')}</p>
                {/* The draft offer reads as its own paragraph in the body's own
                    voice — a notice, not a warning banner. */}
                <p className="text-foreground mt-2">{draftNotice}</p>
              </>
            ) : (
              t('listArchive.bulk.confirmBody')
            )
          }
          confirmLabel={t('listArchive.bulk.confirm')}
          cancelLabel={t('listArchive.bulk.cancel')}
          confirmVariant="default"
          isPending={isPending}
          onConfirm={() => {
            setConfirmOpen(false);
            onConfirm();
          }}
          {...(draftNotice && drafts
            ? {
                secondaryAction: {
                  label: t('listArchive.bulk.drafts.discard', { count: draftCount }),
                  variant: 'destructive' as const,
                  isPending: draftsIsPending,
                  onClick: () => {
                    setConfirmOpen(false);
                    drafts.onDiscard();
                  },
                },
              }
            : {})}
          align="end"
          testId={`${testId}-confirm-popover`}
          trigger={button}
        />
      )}
    </div>
  );
}
